import { usageTracker, UsageTrackerEvent } from "@volumental/analytic";
import {
  Dropdown,
  formatLocale,
  Headline2,
  SegmentedButton,
  SizeTable,
  HighlightBar,
  PillSelector,
} from "@volumental/common";
import type { LengthUnit } from "@volumental/common";
import { useState, useMemo, useEffect } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../store";
import type { SizeChartData } from "../../../types/types";
import { UnitSystem } from "../../../api/gql/graphql";
import { getExistingLocales } from "./utils";

interface SizeChartProps {
  unitSystem: UnitSystem;
  sizeChart: SizeChartData;
}

export function SizeChart({ sizeChart, unitSystem }: SizeChartProps): JSX.Element {
  const { sizeLocale: defaultSizeLocale } = useSessionStore((store) => store);
  const { t } = useTranslation();

  const [unit, setUnit] = useState<LengthUnit>(unitSystem === UnitSystem.Metric ? "centimeters" : "inches");
  const [selectedGenderAge, setSelectedGenderAge] = useState<string>(sizeChart.allCharts[0].genderAge);

  const selectedChart = useMemo(
    () => sizeChart.allCharts.find((chart) => chart.genderAge === selectedGenderAge) ?? sizeChart.allCharts[0],
    [sizeChart.allCharts, selectedGenderAge]
  );

  const availableLocales = useMemo(() => getExistingLocales(selectedChart.sizes), [selectedChart]);

  const [sizeLocale, setSizeLocale] = useState<string>(defaultSizeLocale ?? availableLocales[0]);

  useEffect(() => {
    const currentLocaleIsValid = availableLocales.includes(sizeLocale);

    if (!currentLocaleIsValid && availableLocales.length > 0) {
      setSizeLocale(availableLocales[0]);
    }
  }, [availableLocales, sizeLocale]);

  const unitString = unit === "centimeters" ? "(cm)" : "(in)";
  const localizeGenderedSizeChart = (ga: string): string => {
    if (ga.toLowerCase() === "male") {
      return t("sizeCharts.mens");
    } else if (ga.toLowerCase() === "female") {
      return t("sizeCharts.womens");
    } else if (ga.toLowerCase() === "kids") {
      return t("sizeCharts.kids");
    }
    return t("sizeCharts.unisex");
  };

  const localizeGenderAge = (ga: string): string => {
    if (ga === "male") {
      return t("footwearCategories.male");
    }
    if (ga === "female") {
      return t("footwearCategories.female");
    }
    if (ga === "kids") {
      return t("footwearCategories.kids");
    }
    return t("footwearCategories.unisex");
  };

  const shouldShowGenderAgeSelector = sizeChart.allCharts.length > 1;

  return (
    <>
      <Headline2 className="w-full leading-normal mb-2">
        <HighlightBar>
          {shouldShowGenderAgeSelector ? t("sizeCharts.multiple") : localizeGenderedSizeChart(selectedGenderAge)}
        </HighlightBar>
      </Headline2>
      {shouldShowGenderAgeSelector ? (
        <PillSelector
          selected={selectedGenderAge}
          options={sizeChart.allCharts.map((chart) => ({
            value: chart.genderAge,
            label: localizeGenderAge(chart.genderAge),
          }))}
          onSelect={setSelectedGenderAge}
          groupAriaLabel="gender and age group"
        />
      ) : undefined}
      <div className="flex flex-row items-center justify-between mb-2 gap-2">
        <SegmentedButton
          options={
            unitSystem === UnitSystem.Metric
              ? [
                  { label: "cm", value: "centimeters" },
                  { label: "in", value: "inches" },
                ]
              : [
                  { label: "in", value: "inches" },
                  { label: "cm", value: "centimeters" },
                ]
          }
          selected={unit}
          onSelect={(value) => {
            setUnit(value as LengthUnit);
          }}
          smallContainer
          groupAriaLabel={t("ariaLabels.metric")}
          backgroundColor="var(--color-scanner-accent)"
        />
        <span className="grow">
          <Dropdown
            options={availableLocales.map((locale) => ({
              value: locale,
              label: formatLocale(locale),
            }))}
            selected={sizeLocale}
            onSelect={(selected) => {
              usageTracker.track(UsageTrackerEvent.SizeChartChangeLocale, {
                from: sizeLocale,
                to: selected,
              });
              setSizeLocale(selected);
            }}
            selectedItemAlt={t("imageAlt.selectedLocale")}
            btAlt={t("imageAlt.arrowIcon")}
            groupAriaLabel={t("ariaLabels.locales")}
          />
        </span>
      </div>
      <SizeTable
        sizeList={selectedChart.sizes}
        lengthUnit={unit}
        lengthTitle={`${t("footLength")} ${unitString}`}
        widthTitle={`${t("footWidth")} ${unitString}`}
        sizeLocale={sizeLocale}
      />
    </>
  );
}
